import React, { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import type { UserRole } from '../types'

interface AuthGuardProps {
  children: React.ReactNode
  requiredRoles?: UserRole[]
  requireSuperAdmin?: boolean
  requireEmailVerification?: boolean
  fallbackPath?: string
}

export default function AuthGuard({
  children,
  requiredRoles = [],
  requireSuperAdmin = false,
  requireEmailVerification = false,
  fallbackPath = '/login'
}: AuthGuardProps) {
  const { user, profile, loading, hasRole, isSuperAdmin } = useAuth()
  const location = useLocation()
  const [authCheckCount, setAuthCheckCount] = React.useState(0)
  const [emailVerified, setEmailVerified] = useState<boolean | null>(null)
  const [checkingEmail, setCheckingEmail] = useState(requireEmailVerification)
  const maxAuthChecks = 3

  // Check email verification status
  useEffect(() => {
    async function checkEmailVerification() {
      if (!requireEmailVerification || !user) {
        setCheckingEmail(false)
        return
      }

      try {
        const { data: { user: supabaseUser } } = await supabase.auth.getUser()
        // Email is verified if email_confirmed_at is set
        setEmailVerified(!!supabaseUser?.email_confirmed_at)
      } catch (error) {
        console.error('Error checking email verification:', error)
        setEmailVerified(true) // Fail open to not block users on errors
      } finally {
        setCheckingEmail(false)
      }
    }

    checkEmailVerification()
  }, [user, requireEmailVerification])

  // Effect for auth retry logic - must be before any early returns
  React.useEffect(() => {
    if (!user && authCheckCount < maxAuthChecks) {
      const timer = setTimeout(() => {
        setAuthCheckCount(prev => prev + 1)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [user, authCheckCount, maxAuthChecks])

  // Show loading state while checking authentication
  if (loading || checkingEmail) {
    return (
      <div className="min-h-screen bg-dark-primary text-text-primary flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold mx-auto mb-4"></div>
          <p className="text-text-secondary">Authenticating...</p>
        </div>
      </div>
    )
  }

  // Redirect to login if not authenticated (with retry logic)
  if (!user) {
    if (authCheckCount < maxAuthChecks) {
      return (
        <div className="min-h-screen bg-dark-primary text-text-primary flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold mx-auto mb-4"></div>
            <p className="text-text-secondary">Verifying authentication...</p>
          </div>
        </div>
      )
    }
    return <Navigate to={fallbackPath} state={{ from: location }} replace />
  }

  // Check email verification if required
  if (requireEmailVerification && emailVerified === false) {
    return <Navigate to="/verify-email" state={{ from: location }} replace />
  }

  // Check super admin requirement
  if (requireSuperAdmin && !isSuperAdmin) {
    return <UnauthorizedPage userRole={profile?.role} requiredRoles={requiredRoles} requireSuperAdmin />
  }

  // Check role-based access if roles are specified
  if (requiredRoles.length > 0 && !hasRole(requiredRoles)) {
    return <UnauthorizedPage userRole={profile?.role} requiredRoles={requiredRoles} />
  }

  // Render children if all checks pass
  return <>{children}</>
}

// Unauthorized access component
function UnauthorizedPage({ 
  userRole, 
  requiredRoles,
  requireSuperAdmin = false
}: { 
  userRole?: UserRole
  requiredRoles: UserRole[]
  requireSuperAdmin?: boolean
}) {
  const { signOut } = useAuth()

  return (
    <div className="min-h-screen bg-dark-primary text-text-primary flex items-center justify-center p-8">
      <div className="max-w-md text-center">
        {/* Icon */}
        <div className="text-6xl mb-6">🚫</div>
        
        {/* Title */}
        <h1 className="text-3xl font-bold text-gold mb-4">
          Access Denied
        </h1>
        
        {/* Message */}
        <div className="text-text-secondary mb-6 space-y-2">
          <p>
            {requireSuperAdmin 
              ? "This page is restricted to platform administrators only."
              : "You don't have permission to access this page."}
          </p>
          <p className="text-sm">
            Your role: <span className="text-gold font-medium">{userRole?.toUpperCase() || 'CHECKING...'}</span>
          </p>
          {!requireSuperAdmin && requiredRoles.length > 0 && (
            <p className="text-sm">
              Required roles: <span className="text-gold font-medium">{requiredRoles.map(r => r.toUpperCase()).join(', ')}</span>
            </p>
          )}
          {requireSuperAdmin && (
            <p className="text-sm">
              Required: <span className="text-gold font-medium">SUPER ADMIN</span>
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="space-y-3">
          <button 
            onClick={() => window.history.back()}
            className="btn-primary w-full"
          >
            ← Go Back
          </button>
          
          <button 
            onClick={() => window.location.href = '/'}
            className="btn-secondary w-full"
          >
            🏠 Dashboard
          </button>
          
          <button 
            onClick={signOut}
            className="w-full px-4 py-2 text-text-muted hover:text-red-400 transition-colors"
          >
            Sign Out
          </button>
        </div>

        {/* Help Text */}
        <div className="mt-8 p-4 bg-dark-accent rounded-lg border border-border-gold">
          <p className="text-sm text-text-muted">
            If you believe this is an error, please contact your administrator 
            or reach out to support for assistance.
          </p>
        </div>
      </div>
    </div>
  )
}

// Higher-order component for protecting routes
export function withAuthGuard<P extends object>(
  Component: React.ComponentType<P>,
  requiredRoles?: UserRole[]
) {
  return function AuthGuardedComponent(props: P) {
    return (
      <AuthGuard requiredRoles={requiredRoles}>
        <Component {...props} />
      </AuthGuard>
    )
  }
}

// Hook for checking permissions within components
export function usePermissions() {
  const { hasRole, isAdmin, isStaff, isMember } = useAuth()

  const can = {
    // Admin permissions
    manageUsers: () => isAdmin,
    manageSettings: () => isAdmin,
    manageFacilities: () => isAdmin,
    viewAnalytics: () => isAdmin,
    manageStaff: () => isAdmin,

    // Staff permissions
    manageMembers: () => isStaff,
    manageBilling: () => isStaff,
    manageBookings: () => isStaff,
    viewReports: () => isStaff,
    handleCheckins: () => isStaff,

    // Member permissions
    viewOwnProfile: () => true, // All authenticated users
    viewOwnBookings: () => true,
    viewOwnInvoices: () => true,
    createBookings: () => true,

    // Custom role checking
    access: (roles: UserRole[]) => hasRole(roles),
  }

  return can
}